import { useState } from "react";
import { useLanguageContext } from "../context/LanguageContext";
import EVAApiHelper from "../helpers/EVAApiHelper";

function useEntityTranslations(translations, onChange) {
  const languageContext = useLanguageContext();
  const [languageId, setLanguageId] = useState(
    languageContext.getDefaultLanguageFormValue()?.id,
  );
  const currentTranslationIndex = translations.findIndex(
    (trans) => trans.language_id === languageId,
  );
  const currentTranslation = translations[currentTranslationIndex];

  /**
   * This method will handle the language change event.
   * @param {any} value
   */
  function onLanguageChange(value) {
    if (languageId !== value) {
      setLanguageId(value);
    }
  }

  /**
   * This method will return the translation value for the given key.
   * @param {string} key
   * @returns {string}
   */
  function getValue(key) {
    if (key === "default") return currentTranslation?.default ?? false;
    return currentTranslation?.[key] || "";
  }

  /**
   * This method will handle the entity translation change event for the given key.
   * @param {any} value
   * @param {string} key
   */
  function onTranslationChange(value, key) {
    //Copy the translations (and each translation itself) so the current state is never changed
    const newTranslations = translations.map((trans) => ({ ...trans }));
    let index = newTranslations.findIndex(
      (trans) => trans.language_id === languageId,
    );

    //If the value is a file, then make sure to include the language id in the file name
    if (
      value instanceof File ||
      (Array.isArray(value) && value[0] instanceof File)
    ) {
      value = Array.isArray(value)
        ? value.map((file) => convertFileTranslation(file))
        : convertFileTranslation(value);
    }

    //Add the translation if the index was not found
    if (index === -1) {
      //Don't add a translation without a value
      if (isEmptyValue(value) || value === false) {
        onChange?.(newTranslations);
        return;
      }

      newTranslations.push({
        language_id: languageId,
        default: false,
        [key]: value,
      });
      index = newTranslations.length - 1;
    }
    //Update the translation if the index was found
    else {
      newTranslations[index][key] = value;

      //Remove translation key if the value is empty (default is always kept, because it is required)
      if (key !== "default" && isEmptyValue(value)) {
        delete newTranslations[index][key];
      }
    }

    //If default was changed to true, then set all other translations to false
    if (key === "default" && value === true) {
      newTranslations.forEach((trans, i) => {
        if (i !== index) {
          trans.default = false;
        }
      });
    }

    //Remove translation if all keys are empty (unless it was just made the default, then the user will fill it next)
    const madeDefault = key === "default" && value === true;
    if (
      !madeDefault &&
      Object.keys(newTranslations[index]).filter(
        (key) => key !== "language_id" && key !== "default",
      ).length === 0
    ) {
      newTranslations.splice(index, 1);
    }

    //Update the translations
    onChange?.(newTranslations);
  }

  /**
   * This method will check if the given translation value is empty.
   * @param {any} value
   * @returns {boolean}
   */
  function isEmptyValue(value) {
    return (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    );
  }

  /**
   * This method will make sure the file name contains the language id, for example: "my.photo [_1_].png"
   * @param {File} file
   * @returns {File}
   */
  function convertFileTranslation(file) {
    //Split the name at the last dot, so names with multiple dots keep their extension (a leading dot is not an extension)
    const dotIndex = file.name.lastIndexOf(".");
    const name = dotIndex > 0 ? file.name.slice(0, dotIndex) : file.name;
    const extension = dotIndex > 0 ? file.name.slice(dotIndex) : "";

    return new File([file], `${name} [_${languageId}_]${extension}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }

  /**
   * This method will return the translation error for the given key.
   * @param {string} key
   * @param {object} submitError
   * @returns {string}
   */
  function getTranslationError(key, submitError) {
    let error = EVAApiHelper.getResponseErrorArray(
      `translations.*.${key}`,
      submitError,
      (index) => {
        const languageIndex = languageContext.languages.findIndex(
          (lang) => lang.id === translations[index]?.language_id,
        );
        if (languageIndex === -1) {
          return "";
        } else {
          return languageContext.languages[languageIndex].label;
        }
      },
    );

    //Remove empty translation language tags
    if (error) {
      error = error.replace("<strong><em></em></strong>", "");
      if (error.includes("()")) {
        error = null;
      }
    }

    return error;
  }

  return {
    languageId,
    currentTranslation,
    getValue,
    onLanguageChange,
    onTranslationChange,
    getTranslationError,
  };
}

/**
 * This method will map the response translations to the format used by the component.
 * @param {array} responseTranslations
 * @param {function} mapping
 * @returns {array}
 */
export function mapResponseTranslations(
  responseTranslations,
  mapping = (translation) => translation,
) {
  const map = responseTranslations.map((translation) => ({
    //determine the language id (could be object or id)
    language_id:
      typeof translation.language === "object"
        ? translation.language.id
        : translation.language,
    default: translation.default ?? false,
    ...mapping(translation),
  }));

  //Make sure that null keys are removed from the mapped translations
  map.forEach((translation) => {
    Object.keys(translation).forEach((key) => {
      if (translation[key] === undefined || translation[key] === null) {
        delete translation[key];
      }
    });
  });

  return map;
}

export default useEntityTranslations;
